<template>
  <div class="c-file-upload" v-if="!loading">
    <div v-if="!localFile">
      <p class="c-form__helper-text">
        Please keep file size to a maximum of 16MB.
      </p>
      <input
        :name="name"
        :id="name"
        ref="file"
        class="c-file-upload__input"
        type="file"
        :multiple="multiple"
        @change="onChange"
        :accept="acceptTypes"
      />
      <label
        class="c-file-upload__label c-button c-button--primary"
        :for="name"
      >
        <icon name="upload" width="18" height="18"></icon>
        <span>{{ " " }}Upload from computer</span>
      </label>
      <div v-if="fileTypes && showExtensions">
        <span>Allowed extensions: {{ " " }}</span>
        <span v-for="type in fileTypes">.{{ type + " " }}</span>
      </div>
    </div>

    <div v-else class="c-file-upload__file">
      <div class="c-file-upload__loading-bar" :style="fileLoadingStyle"></div>

      <file-icon :type="localFile.name | extension"></file-icon>

      <div class="c-file-upload__content">
        <div class="c-file-upload__name">{{ localFile.name }}</div>
        <div class="c-file-upload__status" v-if="!uploadComplete">
          Uploading... ({{ uploadPercent }}%)
        </div>
        <div class="c-file-upload__status" v-else>
          Uploaded
          <a v-if="uploadComplete" href="#" @click.prevent="onClickChangeFile"
            ><span v-if="multiple">Add another</span><span v-else>Change</span></a
          >
        </div>
      </div>
    </div>

    <span class="c-form__validation-message" v-if="!localFile && error">{{
      error
    }}</span>

    <message type="danger" v-if="fileUploadErrors.length">
      <h4>There's a problem</h4>
      <p v-for="(uploadError, index) in fileUploadErrors" :key="index">
        {{ uploadError }}
      </p>
    </message>

    <message
      type="warning"
      v-if="
        duplicateFile.id &&
          !duplicateFileResource.id &&
          !hideWarning &&
          !isContentMatch
      "
      class="clearfix"
    >
      <h4>For your information</h4>
      <p>
        A file similar to this has already been uploaded. Do you want to upload
        this file or use the following file:
      </p>

      <p>
        <strong>
          <a href="#" @click.prevent="previewFile">{{ duplicateFile.name }}</a>
        </strong>
        <br />Uploaded at
        <time>{{
          $dayjs(duplicateFile.created_time).format("DD/MM/YYYY HH:mm")
        }}</time>
      </p>
      <p class="u-float-left">
        <base-button :primary="true" @click="useDuplicateFile" type="button"
          >Use existing file</base-button
        >
      </p>

      <p class="u-float-right">
        <base-button
          @click="updateDuplicateBypass"
          type="button"
          v-if="allowReupload"
          >Continue with upload</base-button
        >
      </p>
    </message>

    <message
      type="danger"
      v-else-if="
        duplicateFile &&
          isContentMatch &&
          !duplicateFileResource.id &&
          !hideWarning
      "
    >
      <h4>There's a problem</h4>
      <p>
        The file
        <strong>{{ duplicateFile.file_name }}</strong> has already been
        uploaded. Please use the existing file
      </p>
      <p>
        <base-button :primary="true" @click="useDuplicateFile" type="button"
          >Use existing file</base-button
        >
      </p>
    </message>

    <message type="danger" v-else-if="duplicateFileResource.id">
      <h4>There's a problem</h4>
      <p>
        The file
        <strong>{{ duplicateFile.file_name }}</strong> has already been attached
        to <b>{{ duplicateFileResource.current_version.title }}</b
        >.
      </p>
      <p>
        Please try using another file, it may be that another file with the same
        name, and/or the same content already exists.
      </p>

      <p>
        <router-link :to="routerLink" target="_blank"
          >View the policy here (will open in a new window)</router-link
        >
      </p>
      <br />
    </message>
  </div>
  <loading v-else></loading>
</template>

<script>
export default {
  name: "file-upload",

  props: {
    name: String,
    fileId: [Number, String],
    fileTypes: [Array],
    docType: [Object],
    resource: String,
    error: String,
    bypassDuplicate: {
      type: Boolean,
      default: false,
    },
    resetOnLoad: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showExtensions: {
      type: Boolean,
      default: true,
    },
  },

  $_veeValidate: {
    value() {
      return this.localFile;
    },
  },

  data() {
    return {
      loading: false,
      hideWarning: false,
      fileInfo: [],
      allowReupload: true,
    };
  },

  created() {
    if (this.resetOnLoad) {
      this.$store.commit("videos/reset");
    }

    if (this.fileId) {
      this.getFile();
    }
  },

  computed: {
    isContentMatch() {
      return this.duplicateFileMatch === "content";
    },
    localFile: {
      get() {
        return this.$store.state.videos.localFile;
      },
      set(value) {
        this.$store.commit("videos/setLocalFile", value);
      },
    },

    uploadPercent: {
      get() {
        return this.$store.state.videos.uploadPercent;
      },
      set(value) {
        this.$store.commit("videos/setUploadPercent", value);
      },
    },

    uploadComplete() {
      return this.$store.getters["videos/uploadComplete"];
    },

    fileUploadErrors() {
      return this.$store.state.videos.fileUploadErrors;
    },

    uploadedFile() {
      return this.$store.state.videos.uploadedFile;
    },
    duplicateFileMatch() {
      return this.$store.state.videos.duplicateMatch;
    },

    duplicateFile() {
      return this.$store.state.videos.duplicateFile;
    },

    duplicateFileResource() {
      return this.$store.state.videos.duplicateFileResource;
    },

    acceptTypes() {
      let acceptTypes = "";

      if (this.fileTypes) {
        this.fileTypes.forEach((fileType) => {
          if (this.fileTypes.length >= 2) {
            acceptTypes += `.${fileType},`;
          } else {
            acceptTypes = `.${fileType}`;
          }
        });
      }

      return acceptTypes;
    },

    fileLoadingStyle() {
      return {
        transform: `translateX(${this.uploadPercent - 100}%)`,
      };
    },

    routerLink() {
      if (this.duplicateFileResource) {
        const docType = this.duplicateFileResource.self.includes("/policy/")
          ? "policies"
          : "forms";
        if (this.duplicateFileResource.current_version) {
          return {
            name: "DocumentPreview",
            params: {
              id: this.duplicateFileResource.id,
              docType: docType,
            },
          };
        }

        return {
          name: "DocumentPreview",
          params: {
            id: this.duplicateFileResource.id,
          },
        };
      }
    },
  },

  methods: {
    previewFile() {
      const file = this.duplicateFile;
      this.$store.dispatch("videos/download", file);
    },
    reset() {
      this.loading = false;
      this.$store.commit("videos/reset");
    },

    useDuplicateFile() {
      this.hideWarning = true;
      this.$emit("uploaded", this.uploadedFile);
    },

    updateDuplicateBypass() {
      this.bypassDuplicate = true;
      this.hideWarning = true;

      const file = this.fileInfo[0];

      const formData = new FormData();

      const fileData = {
        file: {
          type: file.type,
          name: file.name,
        },
        duplicate_bypass: true,
      };

      // Add resource boolean to be sent with api request
      // for duplicaton checks
      if (this.resource) {
        fileData[this.resource] = true;
      }

      formData.append("data", JSON.stringify(fileData));
      formData.append("upload", file);

      this.upload(formData);
    },

    onChange(event) {
      this.hideWarning = false;
      this.reset();
      this.fileInfo = [];
      const { files } = this.$refs.file;

      const maxFileSize = this.$store.state.videos.maxFileSize;

      [...files].forEach((file) => {
        if (file.size > maxFileSize) {
          this.reset();
          this.$store.dispatch(
            "displayApiError",
            `${file.name} is too large.  Please keep file size to a maximum of 16MB`
          );
          return;
        }
        this.localFile = file;

        this.fileInfo.push(file);

        if (event.target.files[0]) {
          const formData = new FormData();

          const fileData = {
            file: {
              type: file.type,
              name: file.name,
            },
            duplicate_bypass: this.bypassDuplicate,
          };

          // Add resource boolean to be sent with api request
          // for duplicaton checks
          if (this.resource) {
            fileData[this.resource] = true;
          }

          formData.append("data", JSON.stringify(fileData));
          formData.append("upload", file);
          this.upload(formData);
        }
      });
    },

    onClickChangeFile() {
      this.$emit("changeFile");
      this.reset();
    },

    getFile() {
      this.loading = true;
      this.$store
        .dispatch("videos/read", this.fileId)
        .then((fileResource) => {
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
        });
    },

    upload(fileData) {
      this.$store
        .dispatch("videos/upload", { fileData, resource: this.docType })
        .then((response) => {
          //if we are not bypassing duplicates, and there is a duplicate, reset it
          if (!this.bypassDuplicate) {
            if (this.$store.state.videos.duplicateFile.id === response.file.id) {
              this.reset();
            }
          }
          this.$emit("uploaded", this.uploadedFile);
        })
        .catch((error) => {
          this.$emit("uploadError", error);
        });
    },
  },
};
</script>

<style lang="scss">
.c-file-upload {
  &__input {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
  }

  &__label {
    display: inline-block;
    cursor: pointer;
    margin-right: 0.5em;
    margin-bottom: 0.5em;
  }

  &__file {
    position: relative;
    display: inline-block;
    padding: 1em;
    border: 1px solid $colour-nhs-mid-grey;
    border-radius: 4px;
    color: $colour-nhs-mid-grey;
    overflow: hidden;
  }

  &__loading-bar {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(118, 134, 145, 0.12);
    z-index: -1;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .c-file-icon {
    float: left;
  }

  &__content {
    display: inline-block;
    padding-left: 1em;
  }

  &__status {
    padding-top: 0.5em;
  }
}
</style>
